// React
import React, { useMemo } from 'react';

// Mantine
import {
  AppShell,
  Box,
  ScrollArea,
  TooltipProps,
  useMantineTheme,
  Text,
} from '@mantine/core';

// Refine
import { useMenu } from '@refinedev/core';
import type { RefineThemedLayoutV2SiderProps } from '@refinedev/mantine';
import { useThemedLayoutContext } from '@refinedev/mantine';

//Hooks
import { useFilteredMenuItems, useIsMobile } from 'hooks';

//Store
import { useSiderStore } from 'store/SiderStore';
import { useCurrentUserStore } from 'store/UserStore';

//Components
import { DashboardLink } from './components/DashboardLink';
import { LogoutNavLink } from './components/Logout';
import { SiderMenuItems } from './components/SiderMenuItems';
import { SettingsLink } from './components/SettingsLink';

// Package.json file
import packageJson from '../../../package.json';
import { HamburgerMenu } from './hamburgerMenu';

export const Sider: React.FC<RefineThemedLayoutV2SiderProps> = ({
  activeItemDisabled = false,
}) => {
  const theme = useMantineTheme();
  const { role } = useCurrentUserStore();
  const permissions = role?.permissions ?? [];

  const isMobile = useIsMobile(700);
  const { mobileSiderOpen } = useThemedLayoutContext();
  const { isCollapsed } = useSiderStore();

  const { defaultOpenKeys, menuItems, selectedKey } = useMenu();
  const navOptions = useFilteredMenuItems({ permissions, menuItems });

  const commonNavLinkStyles = useMemo(
    () => ({
      root: {
        display: 'flex',
        marginTop: '0.75rem',
        borderRadius: '10px',
        justifyContent:
          isCollapsed && !mobileSiderOpen ? 'center' : 'flex-start',
        '&[dataActive]': {
          backgroundColor: theme.colors['primary-dark'][0],
          color: theme.colors['primary-dark'][0],
        },
        '&[dataActive]:hover': {
          backgroundColor: theme.colors['primary-dark'][0],
          color: theme.colors['primary-dark'][0],
        },
        icon: {
          marginRight: isCollapsed && !mobileSiderOpen ? '0' : '0.75rem',
        },
        body: {
          display: isCollapsed && !mobileSiderOpen ? 'none' : 'flex',
        },
      },
    }),
    [isCollapsed, mobileSiderOpen]
  );

  const commonTooltipProps: Partial<TooltipProps> = useMemo(
    () => ({
      disabled: !isCollapsed || mobileSiderOpen,
      position: 'right',
      withinPortal: true,
      withArrow: true,
      arrowSize: 8,
      arrowOffset: 12,
      offset: 4,
    }),
    [isCollapsed, mobileSiderOpen]
  );

  const MobileView: React.FC = () => {
    return (
      <AppShell.Section pl={8} component={ScrollArea} mx={'-xs'} px={'xs'} grow>
        <DashboardLink
          isCollapsed={isCollapsed}
          mobileSiderOpen={mobileSiderOpen}
          selectedKey={selectedKey}
          commonNavLinkStyles={commonNavLinkStyles}
          commonTooltipProps={commonTooltipProps}
        />
        <SiderMenuItems
          menuItems={navOptions}
          selectedKey={selectedKey}
          commonNavLinkStyles={commonNavLinkStyles}
          commonToolTipProps={commonTooltipProps}
          activeItemDisabled={activeItemDisabled}
          defaultOpenKeys={defaultOpenKeys}
          isCollapsed={false}
          mobileSiderOpen={mobileSiderOpen}
          key={'sider-menu-items'}
        />
        <SettingsLink
          isCollapsed={isCollapsed}
          mobileSiderOpen={mobileSiderOpen}
          selectedKey={selectedKey}
          commonNavLinkStyles={commonNavLinkStyles}
          commonTooltipProps={commonTooltipProps}
        />
        <LogoutNavLink
          isCollapsed={isCollapsed}
          mobileSiderOpen={mobileSiderOpen}
          commonNavLinkStyles={commonNavLinkStyles}
          commonToolTipProps={commonTooltipProps}
        />
        <Box h="md" aria-hidden="true" />
      </AppShell.Section>
    );
  };

  const DesktopView: React.FC = () => {
    return (
      <>
        <HamburgerMenu />
        <AppShell.Section grow component={ScrollArea} mx="-xs" px="xs">
          <DashboardLink
            isCollapsed={isCollapsed}
            mobileSiderOpen={mobileSiderOpen}
            selectedKey={selectedKey}
            commonNavLinkStyles={commonNavLinkStyles}
            commonTooltipProps={commonTooltipProps}
          />
          <SiderMenuItems
            menuItems={navOptions}
            selectedKey={selectedKey}
            commonNavLinkStyles={commonNavLinkStyles}
            commonToolTipProps={commonTooltipProps}
            activeItemDisabled={activeItemDisabled}
            defaultOpenKeys={defaultOpenKeys}
            isCollapsed={isCollapsed}
            mobileSiderOpen={mobileSiderOpen}
            key={'sider-menu-items'}
          />
          <SettingsLink
            isCollapsed={isCollapsed}
            mobileSiderOpen={mobileSiderOpen}
            selectedKey={selectedKey}
            commonNavLinkStyles={commonNavLinkStyles}
            commonTooltipProps={commonTooltipProps}
          />
          <LogoutNavLink
            isCollapsed={isCollapsed}
            mobileSiderOpen={mobileSiderOpen}
            commonNavLinkStyles={commonNavLinkStyles}
            commonToolTipProps={commonTooltipProps}
          />
          <Box h="md" aria-hidden="true" />
        </AppShell.Section>
        <AppShell.Section
          component="footer"
          data-testid="sider-footer"
          py="xs"
          style={{
            flexShrink: 0,
            borderTop: '1px solid var(--mantine-color-default-border)',
          }}
        >
          <Text ta="center" size="xs">
            version {packageJson.version}
          </Text>
        </AppShell.Section>
      </>
    );
  };

  return isMobile ? <MobileView /> : <DesktopView />;
};
